import { Container, Graphics, Sprite } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { t, type I18nKey } from '@/i18n';
import { getTexture } from '@/assets/resolver';
import { PLAYER } from '@/config/player';
import { cycleGlyph } from './hangar-icons';
import { buildPauseButton } from './pause-button';
import { chamferPanel, label, targetBrackets } from './widgets';
import type { FocusItem, ShopState } from './shop-types';

interface HeaderHooks {
  isSel(item: FocusItem): boolean;
  interactive(node: Container, item: FocusItem): void;
}

/** Header buy-button geometry (design space) — also anchors spatial nav. */
export const HEADER_HEAL_BTN = { x: 372, y: 19, w: 178, h: 40 } as const;
export const HEADER_ARMOR_BTN = { x: 668, y: 19, w: 158, h: 40 } as const;
/** ‖ pause (mouse / touch only — pads use Start), left of the wallet */
const HEADER_MENU_BTN = { x: 846, y: 19, size: 40 } as const;

/**
 * Reroll bar (docs/12 O1): a wide strip glued under the offer cards —
 * cycle icon + the REROLL label + yellow price, one focus target.
 */
export function buildRerollBar(s: ShopState, w: number, selected: boolean): Container {
  const c = new Container();
  const H = 44;
  const canReroll = s.yellow >= s.rerollPrice;
  const frame = new Graphics();
  chamferPanel(frame, w, H, {
    fill: selected ? 0x2a2440 : 0x1a1626,
    stroke: selected ? COLORS.uiAccent : 0x4a4256,
    strokeWidth: selected ? 3 : 2,
    notch: 12,
  });
  c.addChild(frame);

  const rcol = canReroll ? COLORS.uiAccent : 0x5a6168;
  const cycle = cycleGlyph(new Graphics(), 0, 0, 12, rcol);
  cycle.position.set(w / 2 - 80, H / 2);
  c.addChild(cycle);

  const txt = label(t('shop.rerollBtn'), 16, canReroll ? 0xffffff : 0x5a6168, 2);
  txt.anchor.set(0, 0.5);
  txt.position.set(w / 2 - 56, H / 2);
  c.addChild(txt);

  const gem = new Sprite(getTexture('pickup/gem-yellow'));
  gem.anchor.set(0, 0.5);
  gem.scale.set(0.5);
  gem.position.set(w / 2 + 36, H / 2);
  const price = label(String(s.rerollPrice), 16, COLORS.gemYellow, 0);
  price.anchor.set(0, 0.5);
  price.position.set(w / 2 + 62, H / 2);
  if (!canReroll) {
    gem.alpha = 0.45;
    price.alpha = 0.45;
  }
  c.addChild(gem, price);
  if (selected) {
    const br = new Graphics();
    targetBrackets(br, w, H, COLORS.uiAccent, 10, 3);
    c.addChild(br);
  }
  return c;
}

export function buildShopButton(
  text: string, w: number, selected: boolean, enabled: boolean, accent = false,
  price?: number, priceIsRed = false,
): Container {
  const c = new Container();
  const g = new Graphics();
  chamferPanel(g, w, 52, {
    fill: accent ? (selected ? 0x1d4a44 : 0x16332f) : selected ? 0x2a2440 : 0x1a1626,
    stroke: selected ? COLORS.uiAccent : accent ? 0x2e8c80 : 0x4a4256,
    strokeWidth: selected ? 4 : 3,
    notch: 12,
  });
  c.addChild(g);
  const txt = label(text, 17, enabled ? 0xffffff : 0x5a6168, 2);
  txt.anchor.set(0.5);
  txt.position.set(w / 2, 26);
  c.addChild(txt);
  if (price !== undefined) {
    txt.position.set(w / 2 - 26, 26);
    const gem = new Sprite(getTexture(priceIsRed ? 'pickup/gem-red' : 'pickup/gem-yellow'));
    gem.anchor.set(0, 0.5);
    gem.scale.set(0.5);
    gem.position.set(txt.position.x + txt.width / 2 + 10, 26);
    const priceTxt = label(String(price), 16, priceIsRed ? COLORS.gemRed : COLORS.gemYellow, 1);
    priceTxt.anchor.set(0, 0.5);
    priceTxt.position.set(gem.position.x + 26, 26);
    if (!enabled) {
      gem.alpha = 0.45;
      priceTxt.alpha = 0.45;
    }
    c.addChild(gem, priceTxt);
  }
  if (selected) {
    const br = new Graphics();
    targetBrackets(br, w, 52, COLORS.uiAccent, 12, 4);
    c.addChild(br);
  }
  return c;
}

/** Battery cell glyph (hull) — the header readout's own icon. */
export function cellGlyph(g: Graphics, x: number, y: number, color: number): void {
  g.roundRect(x, y - 7, 20, 14, 3).stroke({ color, width: 2 });
  g.roundRect(x + 20, y - 3, 3, 6, 1).fill(color);
  g.roundRect(x + 3, y - 4, 14, 8, 2).fill(color);
}

/** Armor shield glyph — the header readout's own icon. */
export function shieldGlyph(g: Graphics, x: number, y: number, color: number): void {
  g.poly([x + 8, y - 9, x + 16, y - 5, x + 16, y + 2, x + 8, y + 9, x, y + 2, x, y - 5]).fill(color);
}

/**
 * Compact WORDLESS buy button in the header, glued to the vital it raises
 * (docs/09 wave 4): its glyph + the gain ("+25", "+1") + the gem price, or —
 * for stored armor plates — a "×N" stack instead of a price (free).
 */
function buildVitalAction(
  w: number, selected: boolean, enabled: boolean, glyph: (g: Graphics, x: number, y: number) => void,
  gain: string, accent: number, price: { value: number; red: boolean } | { free: number },
): Container {
  const H = HEADER_HEAL_BTN.h;
  const c = new Container();
  const g = new Graphics();
  chamferPanel(g, w, H, {
    fill: selected ? 0x2a2440 : 0x1a1626,
    stroke: selected ? COLORS.uiAccent : enabled ? accent : 0x3a3548,
    strokeWidth: selected ? 3 : 2,
    notch: 10,
  });
  c.addChild(g);
  const row = new Container();
  const icon = new Graphics();
  glyph(icon, 0, 0);
  icon.alpha = enabled ? 1 : 0.45;
  const txt = label(gain, 15, enabled ? 0xffffff : 0x5a6168, 1);
  txt.anchor.set(0, 0.5);
  txt.position.set(28, 0);
  row.addChild(icon, txt);
  let rw = txt.x + txt.width;
  if ('free' in price) {
    const stack = label(`×${price.free}`, 15, COLORS.hudArmor, 1);
    stack.anchor.set(0, 0.5);
    const box = new Graphics();
    box.roundRect(rw + 10, -11, stack.width + 12, 22, 6).fill({ color: COLORS.hudArmor, alpha: 0.18 })
      .stroke({ color: COLORS.hudArmor, width: 1.5 });
    stack.position.set(rw + 16, 0);
    row.addChild(box, stack);
    rw += 22 + stack.width;
  } else {
    const gem = new Sprite(getTexture(price.red ? 'pickup/gem-red' : 'pickup/gem-yellow'));
    gem.anchor.set(0, 0.5);
    gem.scale.set(0.45);
    gem.position.set(rw + 10, 0);
    const priceTxt = label(String(price.value), 15, price.red ? COLORS.gemRed : COLORS.gemYellow, 0);
    priceTxt.anchor.set(0, 0.5);
    priceTxt.position.set(gem.position.x + gem.width + 5, 0);
    if (!enabled) gem.alpha = priceTxt.alpha = 0.45;
    row.addChild(gem, priceTxt);
    rw = priceTxt.x + priceTxt.width;
  }
  if (rw > w - 16) row.scale.set((w - 16) / rw);
  row.position.set((w - rw * row.scale.x) / 2, H / 2);
  c.addChild(row);
  if (selected) {
    const br = new Graphics();
    targetBrackets(br, w, H, COLORS.uiAccent, 9, 3);
    c.addChild(br);
  }
  return c;
}

/**
 * Top bar of the UPGRADE BAY: title, hull + armor vitals with their buy
 * buttons directly beside them (repair / +1 armor — the topmost reach),
 * currency chips on the right; `onMenu` adds the ‖ pause button.
 */
export function buildShopHeader(
  s: ShopState, designW: number, hooks: HeaderHooks, onMenu: (() => void) | null = null,
): Container {
  const c = new Container();
  const header = new Graphics();
  chamferPanel(header, designW - 40, 54, { fill: 0x110e1b, stroke: 0x2a2440, notch: 16 });
  header.position.set(20, 12);
  c.addChild(header);
  const title = label(t('shop.title'), 24, COLORS.playerYellow, 6);
  title.anchor.set(0, 0.5);
  title.position.set(44, 39);
  if (title.width > 190) title.scale.set(190 / title.width);
  c.addChild(title);

  // Hull readout + repair buy right beside it
  const player = s.ctx.player;
  const vitals = new Graphics();
  const bx = 250;
  vitals.roundRect(bx, 31, 26, 16, 3).stroke({ color: COLORS.hudHp, width: 2 });
  vitals.roundRect(bx + 26, 35, 3, 8, 1).fill(COLORS.hudHp);
  const pct = Math.max(0, Math.min(1, player.hp / player.maxHp));
  vitals.roundRect(bx + 3, 34, 20 * pct, 10, 2).fill(COLORS.hudHp);
  const hpTxt = label(`${Math.round(player.hp)}/${player.maxHp}`, 15, COLORS.hudHp, 1);
  hpTxt.anchor.set(0, 0.5);
  hpTxt.position.set(bx + 38, 39);
  c.addChild(vitals, hpTxt);
  const heal = buildVitalAction(
    HEADER_HEAL_BTN.w, hooks.isSel({ kind: 'heal' }),
    s.red >= s.healPrice && player.hp < player.maxHp,
    (g, x, y) => cellGlyph(g, x, y, COLORS.hudHp), `+${PLAYER.cellHp}`, 0x2e8c80,
    { value: s.healPrice, red: true },
  );
  heal.position.set(HEADER_HEAL_BTN.x, HEADER_HEAL_BTN.y);
  hooks.interactive(heal, { kind: 'heal' });
  c.addChild(heal);

  // Armor readout + +1 armor buy right beside it
  const sx = 576;
  vitals.poly([
    sx + 9, 29, sx + 18, 33, sx + 18, 41, sx + 9, 49, sx, 41, sx, 33,
  ]).fill(COLORS.hudArmor);
  const armorTxt = label(`×${player.armor}`, 15, COLORS.hudArmor, 1);
  armorTxt.anchor.set(0, 0.5);
  armorTxt.position.set(sx + 26, 39);
  c.addChild(armorTxt);
  // ONE armor action: stored plates first (free, ×N), then the yellow buy
  const stored = s.armorItems > 0;
  const armor = buildVitalAction(
    HEADER_ARMOR_BTN.w, hooks.isSel({ kind: 'buyArmor' }),
    !s.armorFull && (stored || s.yellow >= s.armorPrice),
    (g, x, y) => shieldGlyph(g, x, y, COLORS.hudArmor), '+1', 0x8c7a2e,
    stored ? { free: s.armorItems } : { value: s.armorPrice, red: false },
  );
  armor.position.set(HEADER_ARMOR_BTN.x, HEADER_ARMOR_BTN.y);
  hooks.interactive(armor, { kind: 'buyArmor' });
  c.addChild(armor);
  if (onMenu) {
    const menu = buildPauseButton(HEADER_MENU_BTN.size, onMenu);
    menu.position.set(HEADER_MENU_BTN.x, HEADER_MENU_BTN.y);
    c.addChild(menu);
  }

  let chipX = designW - 44;
  for (const [tex, nameKey, value, color] of [
    ['pickup/gem-red', 'gem.red', s.red, COLORS.gemRed],
    ['pickup/gem-yellow', 'gem.yellow', s.yellow, COLORS.gemYellow],
  ] as const) {
    const txt = label(String(value), 19, color, 1);
    txt.anchor.set(1, 0.5);
    txt.position.set(chipX, 30);
    const gem = new Sprite(getTexture(tex));
    gem.anchor.set(1, 0.5);
    gem.scale.set(0.7);
    gem.position.set(chipX - txt.width - 6, 36);
    const name = label(t(nameKey as I18nKey), 12, color, 2);
    name.anchor.set(1, 0);
    name.position.set(chipX, 43);
    c.addChild(txt, gem, name);
    chipX -= Math.max(txt.width + 56, name.width + 24);
  }
  return c;
}
